import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoSend } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { isEmpty } from 'lodash-es';
import useUser from '../../../hooks/useUser';
import { ChatMessageType } from '../../../proxies/ChatProxy';
import {
  getConversationMessagesRequest,
  sendChatMessageRequest,
  setMessages,
} from '../../../redux/ducks/ChatDucks';
import type {
  ChatMessageDataType,
  RecentConversationDataType,
} from '../../../redux/ducks/ChatDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import { trackEvent } from '../../../utils/Umami/Umami';
import Spinner from '../../layout/Spinner/Spinner';

export type SendMessagePanelProps = {
  id: string;
  scrollableRef: any;
};

const SendMessagePanel: React.FC<SendMessagePanelProps> = ({
  scrollableRef,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const [message, setMessage] = useState<string>('');
  const [onSend, setOnSend] = useState<boolean>(false);

  const {
    getConversationMessagesResponse,
    messages: receivedMessages,
    conversations,
    currentConversation,
    sendChatMessageResponse,
  } = useSelector((state: RootReducerType) => state.chat);

  const chatConversation = useMemo<
    RecentConversationDataType | undefined
  >(() => {
    if (currentConversation && conversations) {
      const conv = conversations.conversations.filter(
        (c) => c.data.id === currentConversation,
      );

      if (conv.length > 0) {
        return conv[0].data;
      }
    }

    return undefined;
  }, [currentConversation, conversations]);

  const targetConversation = useMemo<RecentConversationDataType | undefined>(
    () => chatConversation,
    [chatConversation],
  );

  useEffect(() => {
    if (onSend && getConversationMessagesResponse) {
      setOnSend(false);
    }
  }, [getConversationMessagesResponse]);

  useEffect(() => {
    if (sendChatMessageResponse?.data && currentConversation) {
      dispatch(
        getConversationMessagesRequest({
          conversationId: currentConversation,
          update: true,
        }),
      );
    }
  }, [sendChatMessageResponse]);

  const messages = useMemo<ChatMessageDataType[]>(() => {
    if (receivedMessages && receivedMessages.messages.length > 0) {
      return [...receivedMessages.messages];
    }

    return [];
  }, [receivedMessages]);

  const onMessageChange = (event: any) => {
    setMessage(event.target.value);
  };

  const onSendMessage = (event: any) => {
    if (targetConversation && !isEmpty(message)) {
      trackEvent('menu-chat-send-message-selected', {
        partner: `partner-${targetConversation.partner}`,
      });
      const referenceId = uuidv4();

      const newMessage: ChatMessageDataType = {
        mine: true,
        sending: true,
        previous: messages[0],
        partner: targetConversation.partner,
        provider: targetConversation.provider,
        data: {
          id: uuidv4(),
          reference_id: referenceId,
          user_id: currentUser?.id || '',
          text: `${message}`,
          timestamp: new Date().getTime(),
          type: ChatMessageType.Text,
        },
        disabled: false,
      };

      if (receivedMessages) {
        dispatch(
          setMessages({
            ...receivedMessages,
            total: messages.length + 1,
            messages: [newMessage, ...(receivedMessages?.messages || [])],
          }),
        );
      }

      dispatch(
        sendChatMessageRequest({
          conversationId: targetConversation.id,
          reference_id: referenceId,
          message,
        }),
      );

      setMessage('');
      setOnSend(true);

      scrollableRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const onKeyUp = (e: any) => {
    if (e?.target && e?.key === 'Enter') {
      e.target.blur();
      onSendMessage(e);
    }
  };

  return (
    <span>
      {!targetConversation?.blocked ? (
        <span className="flex min-h-full min-w-full flex-row gap-0">
          <span className="flex min-h-full grow flex-col items-start justify-center px-2">
            <input
              aria-label={t('static.chat.send_message_label')}
              data-test-id="send-message-panel-input"
              placeholder={t('static.chat.send_message_placeholder')}
              className={`min-w-full rounded-full border border-solid border-white bg-slate-100 p-3 pl-5 text-slate-500 outline-none ${
                !onSend &&
                'hover:border-slate-500 hover:bg-white hover:text-slate-800 focus:border-slate-500 focus:bg-white focus:text-slate-800'
              }`}
              onKeyUp={onKeyUp}
              onChange={onMessageChange}
              value={message}
              disabled={onSend}
            />
          </span>
          <span className="flex min-h-full flex-col items-center justify-center pr-2">
            {onSend ? (
              <span className="rounded-full bg-sky-600 p-3 text-white">
                <Spinner color="fill-sky-600" />
              </span>
            ) : (
              <button
                id="menu-chat-send-message"
                key="menu-chat-send-message"
                aria-label="menu-chat-send-message"
                data-test-id="menu-chat-send-message"
                data-umami-event="menu-chat-send-message-selected"
                className="cursor-pointer rounded-full bg-sky-600 p-3 text-white hover:bg-sky-400"
                type="button"
                onClick={onSendMessage}
              >
                <IoSend size={24} />
              </button>
            )}
          </span>
        </span>
      ) : (
        <span className="flex min-h-full min-w-full flex-row gap-0">
          <span className="grow p-2 text-center font-bold italic text-slate-500">
            {t('static.chat.blocked_conversation_text')}
          </span>
        </span>
      )}
    </span>
  );
};

export default SendMessagePanel;
